import { ReactNode } from 'react';

import { useSlugState } from '../../states/state.store';

import { PlanoBemEstarDetails as PlanoBemEstarPR } from './pr/bem-estar';
import { PlanoBemSeguroDetails as PlanoBemSeguroPR } from './pr/bem-seguro';
import { PlanoEssencialDetails as PlanoEssencialPR } from './pr/essencial';
import {
  brand as brandPR,
  company as companyPR,
  coverage as coveragePR,
  description as descriptionPR,
  email as emailPR,
  footerLinks as footerLinksPR,
  links as linkPR,
  plansLabel as plansLabelPR,
  product as productPR,
  slug as slugPR,
  state as statePR,
  tenant as tenantPR,
} from './pr/general';
import { PlanoMultiassistenciaDetails as PlanoMultiassistenciaPR } from './pr/multiassistencia';
import { PlanoNeoDetails as PlanoNeoDetailsPR } from './pr/neo';
import { PlanoFlexDetails as PlanoFlexDetailsPR } from './pr/flex';

import { PlanoBemEstarDetails as PlanoBemEstarRS } from './rs/bem-estar';
import { PlanoBemSeguroDetails as PlanoBemSeguroRS } from './rs/bem-seguro';
import { PlanoEssencialDetails as PlanoEssencialRS } from './rs/essencial';
import {
  brand as brandRS,
  company as companyRS,
  coverage as coverageRS,
  description as descriptionRS,
  email as emailRS,
  footerLinks as footerLinksRS,
  links as linkRS,
  plansLabel as plansLabelRS,
  product as productRS,
  slug as slugRS,
  state as stateRS,
  tenant as tenantRS,
} from './rs/general';
import { PlanoMultiassistenciaDetails as PlanoMultiassistenciaRS } from './rs/multiassistencia';
import { PlanoNeoDetails as PlanoNeoDetailsRS } from './rs/neo';
import { PlanoFlexDetails as PlanoFlexDetailsRS } from './rs/flex';

import { PlanoBemEstarDetails as PlanoBemEstarSC } from './sc/bem-estar';
import { PlanoBemSeguroDetails as PlanoBemSeguroSC } from './sc/bem-seguro';
import { PlanoEssencialDetails as PlanoEssencialSC } from './sc/essencial';
import {
  brand as brandSC,
  company as companySC,
  coverage as coverageSC,
  description as descriptionSC,
  email as emailSC,
  footerLinks as footerLinksSC,
  links as linkSC,
  plansLabel as plansLabelSC,
  product as productSC,
  slug as slugSC,
  state as stateSC,
  tenant as tenantSC,
} from './sc/general';
import { PlanoMultiassistenciaDetails as PlanoMultiassistenciaSC } from './sc/multiassistencia';
import { PlanoNeoDetails as PlanoNeoDetailsSC } from './sc/neo';
import { PlanoFlexDetails as PlanoFlexDetailsSC } from './sc/flex';

export type usePlanDetails = {
  name: string;
  title: string;
  compactName?: string;
  basePrice?: number;
  description: string;
  heroUri: string;
  icon?: ReactNode;
  thumbnailUri?: string;
  buyButtonLabel: string;
  includedDependents: IncludedDependents;
  benefits: Benefits[];
};

type Benefits = {
  title: string;
  description?: string;
  items?: string[] | { title: string; description: string }[];
  alert?: string;
};

type IncludedDependents = {
  description?: string;
  alert?: string;
};

export const details = {
  PR: {
    essencial: PlanoEssencialPR,
    'bem-estar': PlanoBemEstarPR,
    'bem-seguro': PlanoBemSeguroPR,
    multi: PlanoMultiassistenciaPR,
    neo: PlanoNeoDetailsPR,
    flex: PlanoFlexDetailsPR,
    links: linkPR,
    footerLinks: footerLinksPR,
    email: emailPR,
    company: companyPR,
    tenant: tenantPR,
    state: statePR,
    product: productPR,
    coverage: coveragePR,
    slug: slugPR,
    description: descriptionPR,
    brand: brandPR,
    plansLabel: plansLabelPR,
  },
  RS: {
    essencial: PlanoEssencialRS,
    'bem-estar': PlanoBemEstarRS,
    'bem-seguro': PlanoBemSeguroRS,
    multi: PlanoMultiassistenciaRS,
    neo: PlanoNeoDetailsRS,
    flex: PlanoFlexDetailsRS,
    links: linkRS,
    footerLinks: footerLinksRS,
    email: emailRS,
    company: companyRS,
    tenant: tenantRS,
    state: stateRS,
    product: productRS,
    coverage: coverageRS,
    slug: slugRS,
    description: descriptionRS,
    brand: brandRS,
    plansLabel: plansLabelRS,
  },
  SC: {
    essencial: PlanoEssencialSC,
    'bem-estar': PlanoBemEstarSC,
    'bem-seguro': PlanoBemSeguroSC,
    multi: PlanoMultiassistenciaSC,
    neo: PlanoNeoDetailsSC,
    flex: PlanoFlexDetailsSC,
    links: linkSC,
    footerLinks: footerLinksSC,
    email: emailSC,
    company: companySC,
    tenant: tenantSC,
    state: stateSC,
    product: productSC,
    coverage: coverageSC,
    slug: slugSC,
    description: descriptionSC,
    brand: brandSC,
    plansLabel: plansLabelSC,
  },
};

export const usePlanDetails = () => {
  const { slug: state } = useSlugState();

  const planDetails = findPlanDetails(state);

  return planDetails;
};

function findPlanDetails(uf: string) {
  const ufNormalized = uf.toUpperCase();
  return details[ufNormalized];
}

type FindBenefitsByTitleParams = {
  title: string;
  slug: string;
  uf: string;
};

export const findBenefitsByTitle = ({
  slug,
  title,
  uf,
}: FindBenefitsByTitleParams) => {
  if (!uf) return null;

  const planDetails = findPlanDetails(uf);
  const selected = planDetails[slug] || [];

  const compare = (benefit: Benefits) => benefit.title === title;
  return selected?.benefits.find(compare);
};

type FilterBenefitsByTitleParams = {
  title: string;
  slug: string;
  uf: string;
};

export const filterBenefitsByTitle = ({
  slug,
  title,
  uf,
}: FilterBenefitsByTitleParams) => {
  const planDetails = findPlanDetails(uf);
  const selected = planDetails[slug];

  return selected?.benefits.filter(
    (benefit: Benefits) => benefit.title === title,
  );
};
